import { useQuery } from '@apollo/client'
import BoatIcon from '@mui/icons-material/DirectionsBoat'
import EventIcon from '@mui/icons-material/Event'
import PersonIcon from '@mui/icons-material/Person'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material'
import type { ReactNode } from 'react'
import { useLocale, useTranslate } from 'react-admin'
import { Link } from 'react-router-dom'
import { customerName } from '~/helpers/resources/customers'
import { boatStatusChoices } from '~/types/choices'
import {
  boatLabel,
  countByStatus,
  type DashAppointment,
  type DashboardData,
  type DashBoat,
  type DashChange,
  type DashListing,
  portalFigures,
  RECENT_CUSTOMERS,
  recentlyChanged,
  STATUS_ORDER,
  todos,
} from './data'

/** A card with a heading, an optional link on the right, and its content. */
export const Section = ({
  title,
  hint,
  action,
  children,
  testId,
}: {
  title: string
  hint?: string
  action?: ReactNode
  children: ReactNode
  testId?: string
}) => (
  <Card variant='outlined' sx={{ height: '100%' }} data-testid={testId}>
    <CardContent>
      <Stack
        direction='row'
        sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1, mb: 1 }}
      >
        <Typography variant='h6'>{title}</Typography>
        {action}
      </Stack>
      {hint && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
          {hint}
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
)

const boatHref = (id: number) => `/boats/${id}/show`
const listHref = (status: string) =>
  `/boats?filter=${encodeURIComponent(JSON.stringify({ status }))}`

/* ----------------------------------------------------------- inventory */

/** Boats per status, in the order of a sale; each tile opens the filtered list. */
export const Inventory = ({ boats }: { boats: DashBoat[] }) => {
  const translate = useTranslate()
  const counts = countByStatus(boats)
  const label = (status: string) =>
    translate(boatStatusChoices.find((c) => c.id === status)?.name ?? status)
  // The first four always (a sale's steps); the rest when there are any.
  const tiles = STATUS_ORDER.filter((s, i) => i < 4 || (counts.get(s) ?? 0) > 0)
  const none = counts.get(null) ?? 0
  return (
    <Section title={translate('dashboard.inventory')} testId='dashboard-inventory'>
      <Grid container spacing={1.5}>
        {tiles.map((status) => (
          <Grid key={status} size={{ xs: 6, sm: 4, md: 'grow' }}>
            <Card variant='outlined' sx={{ height: '100%' }}>
              <CardActionArea
                component={Link}
                to={listHref(status)}
                sx={{ height: '100%', p: 1.5 }}
              >
                <Typography variant='h4' component='div'>
                  {counts.get(status) ?? 0}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {label(status)}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
        {none > 0 && (
          <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
            <Card variant='outlined' sx={{ height: '100%', p: 1.5 }}>
              <Typography variant='h4' component='div' color='text.secondary'>
                {none}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {translate('dashboard.no_status')}
              </Typography>
            </Card>
          </Grid>
        )}
      </Grid>
    </Section>
  )
}

/* --------------------------------------------------------- appointments */

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()

/** The next two weeks' appointments: when, what, which boat, who comes, who is in charge. */
export const Upcoming = ({
  appointments,
  users,
}: {
  appointments: DashAppointment[]
  users: DashboardData['users']
}) => {
  const translate = useTranslate()
  const locale = useLocale()
  const now = new Date()
  const tomorrow = new Date(now.getTime() + 86_400_000)
  const day = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
  })
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' })
  const when = (a: DashAppointment) => {
    const start = new Date(a.start)
    const date = sameDay(start, now)
      ? translate('dashboard.today')
      : sameDay(start, tomorrow)
        ? translate('dashboard.tomorrow')
        : day.format(start)
    return `${date}, ${a.allDay ? translate('dashboard.all_day') : time.format(start)}`
  }
  const name = (id: string) => {
    const u = users.find((x) => x.id === id)
    return u?.displayName?.split(' ')[0] || u?.email || ''
  }
  return (
    <Section
      title={translate('dashboard.upcoming')}
      testId='dashboard-upcoming'
      action={
        <MuiLink component={Link} to='/appointments' variant='body2'>
          {translate('dashboard.all_appointments')}
        </MuiLink>
      }
    >
      {appointments.length === 0 ? (
        <Typography color='text.secondary'>{translate('dashboard.upcoming_none')}</Typography>
      ) : (
        <Stack spacing={1.5}>
          {appointments.map((a) => {
            const what = [a.type && translate(`resources.appointments.types.${a.type}`), a.title]
              .filter(Boolean)
              .join(': ')
            const boats = a.appointment_boat.map((x) => x.boat).filter((b) => !!b)
            const customers = a.appointment_customer.map((x) => x.customer).filter((c) => !!c)
            const inCharge = a.appointment_user.map((u) => name(u.user_id)).filter(Boolean)
            return (
              <Stack key={a.id} direction='row' spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <EventIcon color='action' fontSize='small' sx={{ mt: 0.3 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='body2' color='text.secondary'>
                    {when(a)}
                    {a.location ? ` · ${a.location}` : ''}
                  </Typography>
                  <MuiLink component={Link} to={`/appointments/${a.id}`} underline='hover'>
                    {what || translate('resources.appointments.name', { smart_count: 1 })}
                  </MuiLink>
                  {(boats.length > 0 || customers.length > 0) && (
                    <Typography variant='body2'>
                      {boats.map((b, i) => (
                        <span key={b.id}>
                          {i > 0 && ', '}
                          <MuiLink component={Link} to={boatHref(b.id)}>
                            {boatLabel(b)}
                          </MuiLink>
                        </span>
                      ))}
                      {boats.length > 0 && customers.length > 0 && ' · '}
                      {customers.map((c, i) => (
                        <span key={c.id}>
                          {i > 0 && ', '}
                          <MuiLink component={Link} to={`/customers/${c.id}/show`}>
                            {customerName(c)}
                          </MuiLink>
                        </span>
                      ))}
                    </Typography>
                  )}
                  {inCharge.length > 0 && (
                    <Typography variant='caption' color='text.secondary'>
                      {translate('dashboard.in_charge', { names: inCharge.join(', ') })}
                    </Typography>
                  )}
                </Box>
              </Stack>
            )
          })}
        </Stack>
      )}
    </Section>
  )
}

/* ---------------------------------------------------------------- to do */

const SHOWN = 8

/** What the boats being worked on or offered still lack, each boat a link. */
export const Todo = ({ boats }: { boats: DashBoat[] }) => {
  const translate = useTranslate()
  const open = todos(boats)
  return (
    <Section
      title={translate('dashboard.todo')}
      hint={translate('dashboard.todo_hint')}
      testId='dashboard-todo'
    >
      {open.length === 0 ? (
        <Typography color='text.secondary'>{translate('dashboard.todo_none')}</Typography>
      ) : (
        <Stack spacing={2}>
          {open.map(({ key, boats: affected }) => (
            <Box key={key} data-testid={`todo-${key}`}>
              <Stack direction='row' spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                <Chip size='small' color='warning' label={affected.length} />
                <Typography variant='subtitle2'>{translate(`dashboard.todos.${key}`)}</Typography>
              </Stack>
              <Typography variant='body2'>
                {affected.slice(0, SHOWN).map((b, i) => (
                  <span key={b.id}>
                    {i > 0 && ', '}
                    <MuiLink component={Link} to={boatHref(b.id)}>
                      {boatLabel(b)}
                    </MuiLink>
                  </span>
                ))}
                {affected.length > SHOWN && (
                  <Typography component='span' variant='body2' color='text.secondary'>
                    {' '}
                    {translate('dashboard.more', { count: affected.length - SHOWN })}
                  </Typography>
                )}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Section>
  )
}

/* -------------------------------------------------------------- portals */

/** Boatvertizer's latest figures, summed, and the boats people ask about most. */
export const Portals = ({
  listings,
  account,
}: {
  listings: DashListing[]
  account: DashboardData['account']
}) => {
  const translate = useTranslate()
  const locale = useLocale()
  const number = new Intl.NumberFormat(locale)
  const date = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })
  const figures = portalFigures(listings)
  return (
    <Section
      title={translate('dashboard.portals')}
      testId='dashboard-portals'
      action={
        <MuiLink component={Link} to='/feed' variant='body2'>
          {translate('dashboard.to_feed')}
        </MuiLink>
      }
    >
      {!figures.from ? (
        <Typography color='text.secondary'>{translate('dashboard.portals_none')}</Typography>
      ) : (
        <>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {translate('dashboard.portals_period', {
              from: date.format(new Date(figures.from)),
              to: date.format(new Date(figures.to as string)),
            })}
          </Typography>
          <Stack direction='row' spacing={4} sx={{ mb: 2 }}>
            <Box>
              <Typography variant='h4' component='div'>
                {number.format(figures.inquiries)}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {translate('dashboard.inquiries')}
              </Typography>
            </Box>
            <Box>
              <Typography variant='h4' component='div'>
                {number.format(figures.views)}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {translate('dashboard.views')}
              </Typography>
            </Box>
          </Stack>
          <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
            {translate('dashboard.top')}
          </Typography>
          <Stack spacing={0.5}>
            {figures.top.map(({ listing, window }) => (
              <Stack
                key={listing.listing_id}
                direction='row'
                sx={{ justifyContent: 'space-between', gap: 2 }}
              >
                {listing.boat ? (
                  <MuiLink component={Link} to={boatHref(listing.boat.id)} noWrap>
                    {boatLabel(listing.boat)}
                  </MuiLink>
                ) : (
                  <Typography variant='body2' noWrap>
                    {listing.title}
                  </Typography>
                )}
                <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'nowrap' }}>
                  {translate('dashboard.top_line', {
                    inquiries: number.format(window.inquiries ?? 0),
                    views: number.format(window.detailviews ?? 0),
                  })}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
      {account?.last_fetch_at && (
        <Typography
          variant='caption'
          color={account.last_fetch_ok === false ? 'error' : 'text.secondary'}
          sx={{ display: 'block', mt: 1.5 }}
        >
          {account.last_fetch_ok === false
            ? translate('dashboard.portals_failed')
            : translate('dashboard.portals_fetched', {
                when: date.format(new Date(account.last_fetch_at)),
              })}
        </Typography>
      )}
    </Section>
  )
}

/* --------------------------------------------------------------- recent */

/** "vor 2 Stunden", from a timestamp. */
const useAgo = () => {
  const locale = useLocale()
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  return (at: string) => {
    const seconds = (new Date(at).getTime() - Date.now()) / 1000
    const steps: [Intl.RelativeTimeFormatUnit, number][] = [
      ['day', 86_400],
      ['hour', 3_600],
      ['minute', 60],
    ]
    for (const [unit, size] of steps) {
      if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit)
    }
    return rtf.format(0, 'minute')
  }
}

/** The boats and customers changed last — to carry on where one stopped. */
export const Recent = ({
  changes,
  boats,
  users,
}: {
  changes: DashChange[]
  boats: DashBoat[]
  users: DashboardData['users']
}) => {
  const translate = useTranslate()
  const ago = useAgo()
  const byId = new Map(boats.map((b) => [b.id, b]))
  const shown = recentlyChanged(changes, new Set(byId.keys()))
  const customerIds = shown.filter((c) => c.relation === 'customers').map((c) => c.relation_id)
  const { data } = useQuery<{
    customers: {
      id: number
      firstname: string | null
      lastname: string | null
      company: string | null
    }[]
  }>(RECENT_CUSTOMERS, { variables: { ids: customerIds }, skip: customerIds.length === 0 })
  const customers = new Map((data?.customers ?? []).map((c) => [c.id, c]))
  const who = (id: string) => {
    const u = users.find((x) => x.id === id)
    return u?.displayName || u?.email || ''
  }
  const rows = shown
    .map((c) => {
      if (c.relation === 'boats') {
        const b = byId.get(c.relation_id)
        return b && { change: c, icon: <BoatIcon />, label: boatLabel(b), href: boatHref(b.id) }
      }
      const customer = customers.get(c.relation_id)
      return (
        customer && {
          change: c,
          icon: <PersonIcon />,
          label: customerName(customer) || customer.company || `#${customer.id}`,
          href: `/customers/${customer.id}/show`,
        }
      )
    })
    .filter((r) => !!r)
  return (
    <Section title={translate('dashboard.recent')} testId='dashboard-recent'>
      {rows.length === 0 ? (
        <Typography color='text.secondary'>{translate('dashboard.recent_none')}</Typography>
      ) : (
        <Stack spacing={1}>
          {rows.map((r) => (
            <Stack
              key={`${r.change.relation}:${r.change.relation_id}`}
              direction='row'
              spacing={1.5}
              sx={{ alignItems: 'center', color: 'action.active' }}
            >
              {r.icon}
              <Box sx={{ minWidth: 0 }}>
                <MuiLink
                  component={Link}
                  to={r.href}
                  underline='hover'
                  noWrap
                  sx={{ display: 'block' }}
                >
                  {r.label}
                </MuiLink>
                <Typography variant='caption' color='text.secondary'>
                  {ago(r.change.action_tstamp_tx)}
                  {who(r.change.user_id) ? ` · ${who(r.change.user_id)}` : ''}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Section>
  )
}
